import { MouseEventHandler, TouchEventHandler, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TagsBuilder } from '~/components/TagCombobox/TagsBuilder';
import { useJobBulkSelect } from '~/components/job/JobBulkSelectContext';
import { BuildingPlaceholder } from '~/components/ui/BuildingPlaceholder';
import { CardNewV2 } from '~/components/ui/CardNewV2';
import { getOccupationData } from '~/components/ui/CardNewV2/OccupationInformation';
import { Checkbox, CheckboxState } from '~/components/ui/nucleus/Checkbox';
import { JobStatus } from '~/generated/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { TJob } from '~/routes/jobs';
import { Hero } from './Hero';

const { Created, Cancelled } = JobStatus;
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_PX = 10;

type Props = {
  job: TJob;
  small?: boolean;
  selected?: boolean;
  onSelect: (checked: boolean, shift: boolean, single?: boolean) => void;
};

export function JobListItem({ job, small, selected, onSelect }: Props) {
  const { jobId } = useParams();
  const { isMobile } = useBreakpoint();
  const { selectMode, enterSelect } = useJobBulkSelect();
  const showCheckbox = !isMobile || selectMode;
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const longPressFired = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    touchStart.current = null;
  };

  useEffect(() => () => clearLongPress(), []);

  const handleClick: MouseEventHandler = (event) => {
    if (job.status === Cancelled) return;

    if (selectMode) {
      event.preventDefault();
      onSelect(!selected, event.shiftKey);
      return;
    }

    if (event.shiftKey) {
      event.preventDefault();
      onSelect(!selected, true);
      return;
    }
    onSelect(true, false, true);
  };

  const handleTouchStart: TouchEventHandler = (event) => {
    if (!isMobile || selectMode || job.status === Cancelled) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      enterSelect(job.id);
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(10);
      }
    }, LONG_PRESS_MS);
  };

  const handleTouchMove: TouchEventHandler = (event) => {
    if (!touchStart.current || !longPressTimer.current) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - touchStart.current.x);
    const dy = Math.abs(touch.clientY - touchStart.current.y);
    if (dx > LONG_PRESS_MOVE_PX || dy > LONG_PRESS_MOVE_PX) {
      clearLongPress();
    }
  };

  const handleTouchEnd: TouchEventHandler = (event) => {
    if (longPressFired.current) {
      event.preventDefault();
    }
    clearLongPress();
  };

  const occupationData = getOccupationData(job);

  return (
    <div
      className='flex flex-1 items-stretch gap-2 overflow-x-hidden'
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={clearLongPress}
      onContextMenu={(event) => {
        if (isMobile) event.preventDefault();
      }}
    >
      {selected !== undefined && showCheckbox && (
        <div className='mb-3 flex w-6 shrink-0 items-center justify-start overflow-hidden p-1'>
          <Checkbox
            value={selected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
            className='disabled:border-grey-20 disabled:bg-grey-10'
            disabled={job.status === Cancelled}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelect(!selected, event.shiftKey);
            }}
          />
        </div>
      )}

      <CardNewV2
        id={`JobListItem-${job.id}`}
        linkTo={job.status === Created ? `${job.id}/edit` : job.id}
        isSelected={jobId === job.id || (selectMode && selected)}
        titleText={job.name}
        image={job.image ?? <BuildingPlaceholder />}
        showImage={true}
        secondaryText={job.location?.name}
        heroElement={<Hero job={job} small={small} />}
        status={job.status}
        user={job.assignees?.[0]}
        small={small}
        onClick={handleClick}
        showTertiaryTextOnMobile
        tertiaryText={<TagsBuilder tags={job.jobTags ?? []} className='mt-1' />}
        occupationData={occupationData}
      />
    </div>
  );
}
